"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useUsersWrapper = exports.useUsersEntitiesMapByOxygenId = exports.useRolesWrapper = exports.useRolesWithoutInactiveRoles = exports.useRolesEntitiesMapByOxygenId = exports.useRolesEntitiesMapById = exports.useIsLoadingProjectMembers = exports.useCompaniesWrapper = exports.useCompaniesWithoutDeletedCompanies = exports.useCompaniesEntitiesMapByOxygenId = exports.useCompaniesEntitiesMapById = exports.useCachedProjectEntityAlloyHook = void 0;
var _alloyProvider = require("@adsk/alloy-provider");
var _react = require("react");
var _lodash = require("lodash");
/**
 * For the alloy hooks `useUsers`, `useCompanies`, and `useRoles`,
 * they have a peculiar behavior where they will INITIALLY return the complete
 * set of data, since alloy caches it with local storage.
 *
 * But then the hook will kick off the network requests to get the latest data, and
 * the data in the hook will change BACK to the `loading` state.
 *
 * For our purposes, we don't want the user data to load multiple times: once
 * `loading` is false, we want to use that data immediately, and never have the hook return
 * thr loading state again. However, we do want the latest data from the alloy hook.
 *
 * So whenever loading is false from the alloy hook, we use that data.
 *
 * If loading becomes true later for the alloy hook, we don't expose that to consumers of these
 * wrapped functions -- but once they finish loading we return the latest data.
 *
 * This speeds up loading in projects with many users by a LOT -- because we can
 * actrually use the cached alloy function data, we don't need to wait for every user to load.
 *
 * Also, it eliminates a "loading stutter" -- without this wrapper `useIsLoadingProjectMembers`
 * would go from true -> false -> true
 * (because the initial cached values reported loading as false, but then loading would start and become true)
 *  -- we want to to only go from true -> false, so that we can render the page with project entity data and
 * it won't suddenly siwtch back to loading again.
 */
const useCachedProjectEntityAlloyHook = alloyHookFunction => {
  const dataFromAlloyHook = alloyHookFunction();
  const [lastLoadedData, setLastLoadedData] = (0, _react.useState)(dataFromAlloyHook);
  const isLoading = dataFromAlloyHook.loading;
  (0, _react.useEffect)(() => {
    if (!isLoading) {
      setLastLoadedData(dataFromAlloyHook);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  return lastLoadedData;
};
exports.useCachedProjectEntityAlloyHook = useCachedProjectEntityAlloyHook;
const useUsersWrapper = () => {
  return useCachedProjectEntityAlloyHook(_alloyProvider.useUsers);
};
exports.useUsersWrapper = useUsersWrapper;
const useRolesWrapper = () => {
  return useCachedProjectEntityAlloyHook(_alloyProvider.useRoles);
};
exports.useRolesWrapper = useRolesWrapper;
const useCompaniesWrapper = () => {
  return useCachedProjectEntityAlloyHook(_alloyProvider.useCompanies);
};
exports.useCompaniesWrapper = useCompaniesWrapper;
const useRolesWithoutInactiveRoles = () => {
  const {
    data,
    loading
  } = useRolesWrapper();
  // we don't want to display inactive roles or allow them to be selected
  return (0, _react.useMemo)(() => {
    return {
      data: data.filter(_ref => {
        let {
          status
        } = _ref;
        // for roles, the status is marked as "inactive" when it is deleted
        return status !== "inactive";
      }),
      loading
    };
  }, [data, loading]);
};
exports.useRolesWithoutInactiveRoles = useRolesWithoutInactiveRoles;
const useCompaniesWithoutDeletedCompanies = () => {
  const {
    data,
    loading
  } = useCompaniesWrapper();
  // we don't want to display inactive roles or allow them to be selected
  return (0, _react.useMemo)(() => {
    return {
      data: data.filter(_ref2 => {
        let {
          status
        } = _ref2;
        // for companies, the status is marked as "deleted" when it is deleted, otherwise it is null
        return status !== "deleted";
      }),
      loading
    };
  }, [data, loading]);
};
exports.useCompaniesWithoutDeletedCompanies = useCompaniesWithoutDeletedCompanies;
const useEntitiesMap = (useResource, key) => {
  const {
    data,
    loading
  } = useResource();
  return (0, _react.useMemo)(() => {
    return (0, _lodash.keyBy)(data.filter(x => x[key]), key);
    // we can use an incomplete entites map while the data is loading -- especially since it may take a long time to load.
    // Then force a cache miss if the loading state changes, to ensure we have
    // the up to date data as soon as loading stops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, key]);
};
const useUsersEntitiesMapByOxygenId = () => {
  return useEntitiesMap(useUsersWrapper, "autodeskId");
};
exports.useUsersEntitiesMapByOxygenId = useUsersEntitiesMapByOxygenId;
const useRolesEntitiesMapById = () => {
  return useEntitiesMap(useRolesWithoutInactiveRoles, "id");
};
exports.useRolesEntitiesMapById = useRolesEntitiesMapById;
const useRolesEntitiesMapByOxygenId = () => {
  return useEntitiesMap(useRolesWithoutInactiveRoles, "memberGroupId");
};
exports.useRolesEntitiesMapByOxygenId = useRolesEntitiesMapByOxygenId;
const useCompaniesEntitiesMapById = () => {
  return useEntitiesMap(useCompaniesWithoutDeletedCompanies, "id");
};
exports.useCompaniesEntitiesMapById = useCompaniesEntitiesMapById;
const useCompaniesEntitiesMapByOxygenId = () => {
  return useEntitiesMap(useCompaniesWithoutDeletedCompanies, "memberGroupId");
};
exports.useCompaniesEntitiesMapByOxygenId = useCompaniesEntitiesMapByOxygenId;
const useIsLoadingProjectMembers = () => {
  const users = useUsersWrapper();
  const roles = useRolesWrapper();
  const companies = useCompaniesWrapper();
  return [users, roles, companies].some(_ref3 => {
    let {
      loading
    } = _ref3;
    return loading;
  });
};
exports.useIsLoadingProjectMembers = useIsLoadingProjectMembers;
//# sourceMappingURL=projectMembersHooks.js.map